import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ShieldCheck, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { departureStatus, isGuaranteed, formatDepartureRange } from "../../data/holidays";
import { useDepartures } from "../../lib/useDepartures";
import { holidaysLang } from "../../i18n";
import { Chapter, Reveal, useHolidayScroll, EASE } from "./shared";

const INITIAL_VISIBLE = 6;

export default function HolidayDepartures() {
  const { t, i18n } = useTranslation();
  const [all, setAll] = useState(false);
  const DEPARTURES_2027 = useDepartures();
  const rows = all ? DEPARTURES_2027 : DEPARTURES_2027.slice(0, INITIAL_VISIBLE);

  return (
    <section id="departures" className="bg-bone py-24 lg:py-36 scroll-mt-20" data-testid="holidays-departures">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end mb-12 lg:mb-16">
          <div className="lg:col-span-6">
            <Chapter number={t("holidays.season.chapter")} label={t("holidays.departures.eyebrow")} />
            <Reveal delay={0.1}>
              <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-forest leading-[1.0] mt-8">
                {t("holidays.departures.title_a")} <span className="italic">{t("holidays.departures.title_b")}</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="lg:col-span-6 text-base md:text-lg text-muted-foreground leading-relaxed">
            {t("holidays.departures.intro")}
          </Reveal>
        </div>

        <div className="border border-bone-deep bg-white divide-y divide-bone-deep" data-testid="departures-list">
          {rows.map((d, i) => <Row key={d.id} d={d} index={i} lang={holidaysLang(i18n.language)} />)}
        </div>

        <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl" data-testid="departures-note">{t("holidays.departures.note")}</p>
          {DEPARTURES_2027.length > INITIAL_VISIBLE && (
            <button type="button" onClick={() => setAll((v) => !v)} data-testid="departures-toggle" className="self-start text-xs uppercase tracking-wider-3 text-forest link-underline whitespace-nowrap">
              {all ? t("holidays.departures.show_less") : `${t("holidays.departures.show_all")} (${DEPARTURES_2027.length})`}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

function Row({ d, index, lang }) {
  const { t } = useTranslation();
  const scrollTo = useHolidayScroll();
  const status = departureStatus(d);
  const guaranteed = isGuaranteed(d);
  const full = status === "full";
  const pct = Math.round(((d.seats - d.seatsLeft) / d.seats) * 100);
  const seatsText = d.seatsLeft === 1 ? t("holidays.departures.seat_left") : t("holidays.departures.seats_left", { count: d.seatsLeft });

  return (
    <motion.div
      data-testid={`departure-${d.id}`}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, ease: EASE, delay: (index % INITIAL_VISIBLE) * 0.05 }}
      className={`grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 items-center px-5 py-5 lg:px-8 transition-colors ${full ? "opacity-60" : "hover:bg-bone"}`}
    >
      <div className="md:col-span-4">
        <div className="text-[10px] uppercase tracking-wider-3 text-moss">{String(index + 1).padStart(2, "0")} · {t("holidays.departures.package")}</div>
        <div className="font-display text-2xl lg:text-3xl text-forest mt-1 leading-tight">{formatDepartureRange(d, lang)}</div>
      </div>

      <div className="md:col-span-2 flex flex-wrap items-center gap-2">
        <Badge tone={status}>{t(`holidays.departures.status.${status}`)}</Badge>
        {guaranteed && !full && (
          <Badge tone="guaranteed"><ShieldCheck className="w-3 h-3" /> {t("holidays.departures.status.guaranteed")}</Badge>
        )}
      </div>

      <div className="md:col-span-3">
        <div className="flex items-center gap-2 text-sm text-forest">
          <Users className="w-4 h-4 text-moss" />
          <span data-testid={`departure-seats-${d.id}`}>{full ? t("holidays.departures.status.full") : seatsText}</span>
        </div>
        <div className="mt-2 h-1 bg-bone-deep w-full overflow-hidden">
          <div className={`h-full ${full ? "bg-moss" : "bg-terracotta"}`} style={{ width: `${Math.max(pct, 4)}%` }} />
        </div>
      </div>

      <div className="md:col-span-3 md:justify-self-end">
        <button
          type="button"
          data-testid={`departure-cta-${d.id}`}
          onClick={() => scrollTo("enquiry", full ? undefined : `dep:${d.id}`)}
          className={`btn-lift inline-flex items-center gap-2 px-5 py-3 min-h-[44px] text-xs uppercase tracking-wider-3 whitespace-nowrap ${
            full ? "border border-forest text-forest hover:bg-forest hover:text-bone" : "bg-forest text-bone hover:bg-forest-deep"
          }`}
        >
          {full ? t("holidays.departures.full_cta") : t("holidays.departures.cta")} <ArrowUpRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
}

function Badge({ tone, children }) {
  const styles = {
    available: "bg-forest text-bone",
    last: "bg-terracotta text-white",
    full: "bg-moss text-white",
    guaranteed: "border border-forest text-forest",
  };
  return <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-wider-2 ${styles[tone]}`}>{children}</span>;
}
